<template>
  <v-container fluid justify-start>

    <!-- Page Title -->
    <app-page-title
      v-bind:title="'Herdtrax Assistant'"
      v-bind:icon="'assistant'"
    />

    <v-card>
      <v-row justify="center" v-if="headers.length === 0 && !isLoading">
        <v-card>
          <v-list-item class="pa-2">
            <v-list-item-action>
              <h2 class="headline">
                No Herdtrax accounts found
              </h2>
            </v-list-item-action>
            <v-list-item-action>
              <v-icon large class="ml-2">
                sentiment_very_dissatisfied
              </v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-card>
      </v-row>

      <template v-else>

        <!-- Search Text -->
        <v-toolbar dark :color="appColor_Medium">
          <v-text-field
            class="pt-0 pb-0"
            v-model="search"
            append-icon="search"
            label="Search..."
            single-line
            hide-details
            clearable
            >
          </v-text-field>
          <v-spacer />
        </v-toolbar>

        <!-- Data Table -->
        <v-data-table
          :headers="headers"
          :items="accounts"
          :loading="isLoading"
          :search="search"
          :no-data-text="noDataText"
          item-key="accountId"
          mobile-breakpoint="500"
          class="elevation-1"
          >

          <v-progress-linear v-slot:progress :color="appColor_Progress" indeterminate></v-progress-linear>

          <template v-slot:item.isStaged="{ item }">
            <v-btn v-if="item.isStaged" small @click="showStageDialog(item)">Restage</v-btn>
            <v-btn v-else small color="info" @click="showStageDialog(item)">Stage</v-btn>
          </template>

          <template v-slot:item.stageControl.animalCount="{ item }">
            <span class="mr-4">
              {{ item.stageControl.animalCount }}
            </span>
          </template>

          <template v-slot:item.isAnalysis="{ item }">
            <v-btn 
                v-if="item.isAnalysis || !item.isStaged"
                :disabled="!item.isStaged"
                icon
                outlined
                @click="downloadAnalysis(item.accountId)"
                ><v-icon color="grey darken-3">file_download</v-icon>
              </v-btn>
              <v-icon v-else color="grey darken-3">thumb_up</v-icon>
          </template>

          <template v-slot:item.isImported="{ item }">
            <v-btn
              v-if="item.isImported"
              :disabled="!item.isStaged"
              color="default"
              small
              @click="importData(item)"
              >Reimport
            </v-btn>
            <v-btn
              v-else
              :disabled="!item.isStaged"
              color="success"
              small
              @click="importData(item)"
              >Import
            </v-btn>
          </template>

          <template v-slot:item.importCount="{ item }">
            <span class="mr-4">
              <router-link to="/herdtraxAssistant">
                <span @click="displayImportResult(item)">
                  {{ item.importCount }}
                </span>
              </router-link>
            </span>
          </template>

          <template v-slot:item.assessment="{ item }">
            <v-btn
              :disabled="!item.isImported"
              icon
              outlined
              @click="downloadAssessment(item)"
              ><v-icon color="grey darken-3">file_download</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </template>
    </v-card>

    <!-- Stage Dialog -->
    <v-dialog
      max-width="700px"
      v-model="stageDialog"
      :persistent="true"
      >
      <v-card>
        <v-toolbar dark flat :color="errorText.length === 0 ? appColor_Light : appColor_Error">
          <v-btn icon dark :disabled="isStaging" @click="closeStager">
            <v-icon>close</v-icon>
          </v-btn>
          <v-toolbar-title class="subheading ml-2">{{ stageAccount.accountId }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-toolbar-items>
              <v-btn dark text
                :loading="isStaging"
                :disabled="isStaging || !isStageFile"
                @click="stageData"
                >{{ stageButtonText }}
                <span slot="loader" class="custom-loader">
                  <v-icon>cached</v-icon>
                </span>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text class="pb-0">
          <hx-data-stager
            ref="stager"
            v-bind:stageAccount="stageAccount"
            v-on:staging="isStaging = true"
            v-on:staged="stageComplete"
            v-on:filePicked="isStageFile = true"
            >
          </hx-data-stager>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Import Dialog -->
    <v-dialog
      max-width="650px"
      v-model="importDialog"
      :persistent="true"
      >
      <v-card>
        <v-toolbar dark flat :color="errorText.length === 0 ? appColor_Light : appColor_Error">
          <v-btn icon dark :disabled="isImporting" @click="closeImport">
            <v-icon>close</v-icon>
          </v-btn>
          <v-toolbar-title class="subheading">{{ accountId }}</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text class="pa-0">
          <hx-import-result
            v-bind:importResult="importResult"
            v-bind:isImporting="isImporting"
            v-bind:errorText="errorText"
            >
          </hx-import-result>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog
      v-model="errorDialog"
      max-width="500px"
      >
      <app-error
        :error-title="errorTitle"
        :error-text="errorText"
        @close="errorDialog = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import AppPageTitle from '@/components/AppPageTitle'
import HxDataStager from '@/components/HxDataStager'
import HxImportResult from '@/components/HxImportResult'
import { mapGetters } from 'vuex'
import { AppColor } from '@/config.js'

export default {
  name: 'HerdtraxManager',
  components: {
    AppPageTitle,
    HxDataStager,
    HxImportResult
  },
  data () {
    return {
      appColor_Light: AppColor.Light,
      appColor_Medium: AppColor.Medium,
      appColor_Background: AppColor.Background,
      appColor_Error: AppColor.Error,
      appColor_Progress: AppColor.Progress,

      isLoading: true,
      isStaging: false,
      isImporting: false,
      isAnalyzing: false,

      isStageFile: false,
      stageDialog: false,
      analysisDialog: false,
      importDialog: false,

      search: '',
      stageAccountDefault: {
        fileName: '',
        fileUrl: '',
        dataFile: '',
        accountId: null,
        userId: null,
        csvFilename: '',
        isStaged: false,
        animalCount: 0
      },
      stageAccount: {
        fileName: '',
        fileUrl: '',
        dataFile: '',
        accountId: null,
        userId: null,
        csvFilename: '',
        isStaged: false,
        animalCount: 0
      },
      selectedIndex: -1,
      accounts: [],
      headers: [
        {
          text: 'Account',
          value: 'accountId',
          align: 'left',
          width: '40%'
        },
        {
          text: 'Staging',
          value: 'isStaged',
          align: 'center',
          width: '10%',
          class: 'pr-0'
        },
        {
          text: 'Staged',
          value: 'stageControl.animalCount',
          align: 'right',
          width: '10%'
        },
        {
          text: 'Analysis',
          value: 'isAnalysis',
          align: 'center',
          width: '10%',
          class: 'pr-0'
        },
        {
          text: 'Import',
          value: 'isImported',
          align: 'center',
          width: '10%',
          class: 'pr-0'
        },
        {
          text: 'Imported',
          value: 'importCount',
          align: 'right',
          width: '10%',
        },
        {
          text: 'Assessment',
          value: 'assessment',
          align: 'center',
          width: '10%',
          sortable: false
        }
      ],
      analysisResult: null,
      importResult: null,
      accountId: null,

      // error handling
      isError: false,
      errorDialog: false,
      errorTitle: 'Internal Error',
      errorText: null
    }
  },
  computed: {
    ...mapGetters(['profile']),
    noDataText () {
      if (this.isLoading) return 'One moment...'

      return this.accounts.length === 0
        ? 'No Herdtrax accounts found'
        : ''
    },
    stageButtonText: function () {
      return this.stageAccount.isStaged
        ? 'Restage' : 'Stage'
    }
  },
  methods: {
    loadData () {
      const self = this
      self.isLoading = true
      self.isError = false
      self.errorText = ''
      appService.getHxManagerAccounts().then(data => {
        if (data.errorSeverity > 0) {
          self.isError = true
          self.errorText = '<v-flex class="text-xs-left">' + data.errorMessage + '</v-flex>'
          self.errorDialog = true
          self.isLoading = false
        } else {
          self.accounts = data.accounts
          self.isLoading = false
        }
      })
    },

    showStageDialog (account) {
      this.selectedIndex = this.accounts.indexOf(account)
      this.stageAccount = this.getStageAccountModel(account)
      this.stageDialog = true
    },

    stageData () {
      this.$refs.stager.stageData()
    },

    getNumberFormat (x) {
      if (typeof x === 'undefined' || x === null) return ''
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    stageComplete ({ stageControl }) {
      this.isStaging = false

      if (stageControl !== null) {
        this.accounts[this.selectedIndex].isStaged = true
        this.accounts[this.selectedIndex].stageControl.id = stageControl.id
        this.accounts[this.selectedIndex].stageControl.csvFilename = stageControl.csvFilename
        this.accounts[this.selectedIndex].stageControl.animalCount = stageControl.animalCount
        this.accounts[this.selectedIndex].isImported = false
        this.accounts[this.selectedIndex].importCount = null
        this.accounts[this.selectedIndex].isAnalysis = stageControl.isAnalysis
      }
    },

    closeStager () {
      const self = this
      self.stageDialog = false
      setTimeout(() => {
        self.selectedIndex = -1
        self.isStageFile = false
        self.stageAccount = Object.assign({}, self.stageAccountDefault)
        self.$refs.stager.resetInput()
      }, 300)
    },

    getStageAccountModel (account) {
      return {
        fileName: '',
        fileUrl: '',
        dataFile: '',
        accountId: account.accountId,
        userId: this.profile.id,
        csvFilename: account.stageControl !== null
          ? account.stageControl.csvFilename
          : '',
        isStaged: account.isStaged,
        animalCount: account.stageControl !== null
          ? account.stageControl.animalCount
          : 0
      }
    },

    importData (account) {
      const self = this

      if (account.stageControl.id === null) return
      if (account.stageControl.id <= 0) return

      self.selectedIndex = this.accounts.indexOf(account)
      self.isImporting = true
      self.importDialog = true
      self.accountId = account.accountId

      appService.importHerdtraxData(self.accountId, self.profile.id, false).then((data) => {
        self.isImporting = false
        self.importResult = data.importResult
        self.errorText = data.errorMessage
        if (self.importResult !== null) {
          if (self.importResult.importCount > 0) {
            self.accounts[this.selectedIndex].isImported = true
            self.accounts[this.selectedIndex].importCount = self.importResult.importCount
          }
        }
      })
    },

    displayImportResult (account) {
      const self = this

      if (account.stageControl.id === null) return
      if (account.stageControl.id <= 0) return

      self.selectedIndex = this.accounts.indexOf(account)
      self.isImporting = true
      self.importDialog = true
      self.accountId = account.accountId

      appService.getHerdtraxImportResult(account.stageControl.id, self.profile.id).then((data) => {
        self.isImporting = false
        self.importResult = data.importResult
        self.errorText = data.errorMessage
      })
    },

    closeImport () {
      this.importDialog = false
      this.isImporting = false

      setTimeout(() => {
        this.selectedIndex = -1
        this.importResult = null
        this.errorText = ''
      }, 300)
    },

    downloadAssessment (account) {
      appService.getHerdtraxAssessmentExcel(account.accountId, account.stageControl.id).then(() => {
      })
    },

    downloadAnalysis (accountId) {
      appService.getHerdtraxAnalysisExcel(accountId).then(() => {
      })
    }
  },

  created () {
    this.loadData()
  }
}
</script>